import type { CSSProperties } from "react";
import { contrastTextColorHex } from "./colorUtils";
import { googleFontsStylesheetUrl } from "./googleFonts";

export interface WebsiteDesignSettingsTheme {
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
  };
  typography?: {
    headingFont?: string;
    headingColor?: string;
    bodyFont?: string;
    bodyColor?: string;
  };
}

export function parseWebsiteDesignSettingsTheme(
  raw: unknown,
): WebsiteDesignSettingsTheme | null {
  if (!raw || typeof raw !== "object") return null;

  const doc = raw as Record<string, unknown>;
  const colorsRaw = doc.colors;
  const typographyRaw = doc.typography;

  const colors =
    colorsRaw && typeof colorsRaw === "object"
      ? {
          primary: optionalThemeString((colorsRaw as Record<string, unknown>).primary),
          secondary: optionalThemeString((colorsRaw as Record<string, unknown>).secondary),
          accent: optionalThemeString((colorsRaw as Record<string, unknown>).accent),
          background: optionalThemeString((colorsRaw as Record<string, unknown>).background),
        }
      : undefined;

  const typography =
    typographyRaw && typeof typographyRaw === "object"
      ? {
          headingFont: optionalThemeString((typographyRaw as Record<string, unknown>).headingFont),
          headingColor: optionalThemeString(
            (typographyRaw as Record<string, unknown>).headingColor,
          ),
          bodyFont: optionalThemeString((typographyRaw as Record<string, unknown>).bodyFont),
          bodyColor: optionalThemeString((typographyRaw as Record<string, unknown>).bodyColor),
        }
      : undefined;

  const hasColors = colors && Object.values(colors).some(Boolean);
  const hasTypography = typography && Object.values(typography).some(Boolean);
  if (!hasColors && !hasTypography) return null;

  return {
    ...(hasColors ? { colors } : {}),
    ...(hasTypography ? { typography } : {}),
  };
}

function optionalThemeString(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function websiteDesignCssVariables(
  settings: WebsiteDesignSettingsTheme | null | undefined,
): CSSProperties {
  if (!settings) return {};

  const vars: Record<string, string> = {};
  const { colors, typography } = settings;

  if (colors?.primary?.trim()) {
    vars["--primary"] = colors.primary.trim();
    vars["--primary-foreground"] = contrastTextColorHex(colors.primary.trim());
  }
  if (colors?.secondary?.trim()) {
    vars["--secondary"] = colors.secondary.trim();
    vars["--secondary-foreground"] = contrastTextColorHex(colors.secondary.trim());
  }
  if (colors?.accent?.trim()) {
    vars["--accent"] = colors.accent.trim();
    vars["--accent-foreground"] = contrastTextColorHex(colors.accent.trim());
  }
  if (colors?.background?.trim()) {
    vars["--background"] = colors.background.trim();
  }
  if (typography?.bodyColor?.trim()) {
    vars["--foreground"] = typography.bodyColor.trim();
  }
  if (typography?.headingFont?.trim()) {
    vars["--heading-font"] = `"${typography.headingFont.trim()}", sans-serif`;
  }
  if (typography?.headingColor?.trim()) {
    vars["--heading-color"] = typography.headingColor.trim();
  }
  if (typography?.bodyFont?.trim()) {
    vars["--body-font"] = `"${typography.bodyFont.trim()}", sans-serif`;
  }

  return vars as CSSProperties;
}

export function websiteDesignBodyFontFamily(
  settings: WebsiteDesignSettingsTheme | null | undefined,
): string | undefined {
  const bodyFont = settings?.typography?.bodyFont?.trim();
  if (!bodyFont) return undefined;
  return `"${bodyFont}", sans-serif`;
}

export function uniqueGoogleFontFamilies(
  settings: WebsiteDesignSettingsTheme | null | undefined,
): string[] {
  if (!settings?.typography) return [];
  const families = [
    settings.typography.headingFont?.trim(),
    settings.typography.bodyFont?.trim(),
  ].filter((f): f is string => Boolean(f));
  return [...new Set(families)];
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
}

export function websiteDesignThemeHeadMarkup(
  settings: WebsiteDesignSettingsTheme | null | undefined,
): string {
  const fontFamilies = uniqueGoogleFontFamilies(settings);
  const fontLinks = fontFamilies
    .map((family) => {
      const href = googleFontsStylesheetUrl({ family });
      if (!href) return "";
      return `<link rel="stylesheet" href="${escapeHtmlAttr(href)}">`;
    })
    .filter(Boolean)
    .join("");

  const cssVars = websiteDesignCssVariables(settings);
  const rootDeclarations = Object.entries(cssVars)
    .filter((entry): entry is [string, string] => typeof entry[1] === "string")
    .map(([name, value]) => `${name}:${value}`)
    .join(";");

  const bodyFontFamily = websiteDesignBodyFontFamily(settings);
  const styleRules = [
    rootDeclarations ? `:root{${rootDeclarations}}` : "",
    bodyFontFamily ? `body{font-family:${bodyFontFamily}}` : "",
    "h1,h2,h3,h4,h5,h6{font-family:var(--heading-font,inherit);color:var(--heading-color,inherit)}",
  ]
    .filter(Boolean)
    .join("");

  if (!fontLinks && !styleRules) return "";

  const styleBlock = styleRules
    ? `<style id="website-design-theme">${styleRules}</style>`
    : "";

  return `${fontLinks}${styleBlock}`;
}

export function injectWebsiteDesignThemeIntoHtml({
  html,
  settings,
}: {
  html: string;
  settings: WebsiteDesignSettingsTheme | null | undefined;
}): string {
  const markup = websiteDesignThemeHeadMarkup(settings);
  if (!markup) return html;

  const headClose = /<\/head>/i;
  if (headClose.test(html)) {
    return html.replace(headClose, `${markup}</head>`);
  }

  const htmlOpen = /<html[^>]*>/i;
  if (htmlOpen.test(html)) {
    return html.replace(htmlOpen, (match) => `${match}<head>${markup}</head>`);
  }

  return `<!DOCTYPE html><html lang="en"><head><meta charset="utf-8">${markup}</head><body>${html}</body></html>`;
}
